// src/components/Guilds.js

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import LoadingSpinner from "./LoadingSpinner";
import { apiJson } from "./apiFetch";

const API_BASE = process.env.REACT_APP_API_URL || "";

const GUILD_CREATE_COST = 500;

const EMBLEMS = [
  "shield",
  "moon",
  "sun",
  "crown",
  "sword",
  "flame",
  "cobra",
  "scarab",
];

/* =========================================================
 * HELPERS
 * ========================================================= */

function errorMessage(code) {
  const messages = {
    INVALID_GUILD_NAME:
      "Guild name must contain between 3 and 30 valid characters.",

    INVALID_GUILD_TAG:
      "Guild tag must contain between 2 and 6 letters or numbers.",

    ALREADY_IN_GUILD:
      "You are already a member of a Guild.",

    GUILD_NAME_ALREADY_EXISTS:
      "This Guild name is already being used.",

    GUILD_TAG_ALREADY_EXISTS:
      "This Guild tag is already being used.",

    INSUFFICIENT_FUNDS:
      "You do not have enough Runes to create this Guild.",

    BALANCE_CHANGED:
      "Your Rune balance changed. Please try again.",

    USER_NOT_FOUND:
      "Your account could not be found.",

    GUILD_CREATE_FAILED:
      "The Guild could not be created. Please try again.",

    GUILD_LOAD_FAILED:
      "The Guild could not be loaded.",

    NOT_IN_GUILD:
      "You are not currently in a Guild.",

    GUILD_PERMISSION_DENIED:
      "You do not have permission to perform this action.",

    GUILD_MEMBER_NOT_FOUND:
      "This member could not be found.",

    INVALID_GUILD_ROLE:
      "Invalid Guild role.",

    CANNOT_CHANGE_OWN_ROLE:
      "You cannot change your own role.",

    CANNOT_CHANGE_LEADER_ROLE:
      "The Leader role cannot be changed here.",

    GUILD_ROLE_CHANGE_FAILED:
      "Could not change this member's role.",
  };

  return (
    messages[code] ||
    "Something went wrong. Please try again."
  );
}

/* =========================================================
 * PAGE HEADER
 * ========================================================= */

function GuildPageHeader({
  title = "GUILDS",
  onBack,
}) {
  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        minHeight: 62,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
        flexShrink: 0,
      }}
    >
      <button
        type="button"
        onClick={onBack}
        aria-label="Back"
        style={{
          position: "absolute",
          left: 3,
          top: "50%",
          transform: "translateY(-50%)",

          width: 48,
          height: 48,

          display: "flex",
          alignItems: "center",
          justifyContent: "center",

          border: 0,
          padding: 0,

          background: "transparent",
          color: "#fff",

          fontSize: 46,
          lineHeight: "42px",
          fontWeight: 900,

          textShadow:
            "2px 2px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000",

          cursor: "pointer",
          zIndex: 2,
        }}
      >
        ‹
      </button>

      <div
        className="shadow1"
        style={{
          color: "#fff",
          fontSize: 30,
          lineHeight: "34px",
          fontWeight: 900,
          textAlign: "center",
        }}
      >
        {title}
      </div>
    </div>
  );
}

/* =========================================================
 * EMBLEM
 * ========================================================= */

function GuildEmblem({
  emblem = "shield",
  size = 88,
}) {
  const [failed, setFailed] =
    useState(false);

  useEffect(() => {
    setFailed(false);
  }, [emblem]);

  if (failed) {
    return (
      <div
        style={{
          width: size,
          height: size,

          display: "flex",
          alignItems: "center",
          justifyContent: "center",

          margin: "0 auto",

          borderRadius: "50%",

          background:
            "radial-gradient(circle, #7d4c21 0%, #351708 72%)",

          border:
            "3px solid rgba(255,211,91,.75)",

          boxShadow:
            "0 5px 8px rgba(0,0,0,.45)",

          color: "#ffd76b",

          fontSize: Math.max(
            28,
            Math.round(size * 0.42)
          ),

          fontWeight: 900,
        }}
      >
        ⚔
      </div>
    );
  }

  return (
    <div
      style={{
        width: size,
        height: size,

        display: "flex",
        alignItems: "center",
        justifyContent: "center",

        margin: "0 auto",
      }}
    >
      <img
        src={`/guilds/${emblem}.png`}
        alt=""
        draggable={false}
        onError={() =>
          setFailed(true)
        }
        style={{
          display: "block",
          maxWidth: "100%",
          maxHeight: "100%",
          objectFit: "contain",

          filter:
            "drop-shadow(0 5px 4px rgba(0,0,0,.55))",
        }}
      />
    </div>
  );
}

/* =========================================================
 * UI HELPERS
 * ========================================================= */

function SectionTitle({
  children,
}) {
  return (
    <div
      style={{
        marginBottom: 7,
        color: "#ffdb78",
        fontSize: 15,
        lineHeight: "18px",
        fontWeight: 800,
        textTransform: "uppercase",
        textAlign: "left",

        textShadow:
          "1px 1px 0 rgba(0,0,0,.7)",
      }}
    >
      {children}
    </div>
  );
}

function TextInput({
  label,
  value,
  onChange,
  placeholder = "",
  maxLength,
  type = "text",
  min,
}) {
  return (
    <div
      style={{
        width: "100%",
        marginBottom: 16,
      }}
    >
      <SectionTitle>
        {label}
      </SectionTitle>

      <input
        type={type}
        value={value}
        min={min}
        maxLength={maxLength}
        placeholder={placeholder}
        onChange={onChange}
        style={{
          width: "100%",
          height: 48,
          boxSizing: "border-box",
          padding: "0 13px",
          borderRadius: 9,

          border:
            "3px solid rgba(91,48,23,.9)",

          background: "#120906",
          color: "#fff",
          fontSize: 17,
          outline: "none",

          boxShadow:
            "inset 0 2px 6px rgba(0,0,0,.45)",
        }}
      />
    </div>
  );
}

function SelectInput({
  label,
  value,
  onChange,
  children,
}) {
  return (
    <div
      style={{
        width: "100%",
        marginBottom: 16,
      }}
    >
      <SectionTitle>
        {label}
      </SectionTitle>

      <select
        value={value}
        onChange={onChange}
        style={{
          width: "100%",
          height: 48,
          boxSizing: "border-box",
          padding: "0 12px",
          borderRadius: 9,

          border:
            "3px solid rgba(91,48,23,.9)",

          background: "#120906",
          color: "#fff",
          fontSize: 16,
          outline: "none",
        }}
      >
        {children}
      </select>
    </div>
  );
}

function GuildCard({
  children,
  style = {},
}) {
  return (
    <div
      style={{
        width: "100%",
        boxSizing: "border-box",
        padding: 16,

        border:
          "3px solid rgba(126,67,31,.95)",

        borderRadius: 12,

        background:
          "linear-gradient(180deg, rgba(139,91,49,.96) 0%, rgba(112,69,35,.97) 100%)",

        boxShadow:
          "0 5px 0 rgba(70,31,13,.72)",

        ...style,
      }}
    >
      {children}
    </div>
  );
}

/* =========================================================
 * NO GUILD
 * ========================================================= */

function NoGuild({
  onCreate,
  onFindGuild,
}) {
  return (
    <GuildCard
      style={{
        paddingTop: 30,
        paddingBottom: 28,
        textAlign: "center",
      }}
    >
      <GuildEmblem
        emblem="shield"
        size={110}
      />

      <div
        className="shadow1"
        style={{
          marginTop: 16,
          color: "#fff",
          fontSize: 26,
          lineHeight: "30px",
          fontWeight: 900,
        }}
      >
        Stronger Together
      </div>

      <div
        style={{
          maxWidth: 315,

          margin:
            "10px auto 26px",

          color:
            "rgba(255,255,255,.82)",

          fontSize: 15,
          lineHeight: "21px",

          textShadow:
            "1px 1px rgba(0,0,0,.5)",
        }}
      >
        Create or join a Guild,
        fight together, complete
        objectives and earn rewards
        with your team.
      </div>

      <motion.button
        type="button"
        whileTap={{
          scale: 0.98,
        }}
        onClick={onCreate}
        className="game-button yellow-button"
        style={{
          width: "100%",
          maxWidth: 330,
          margin: "0 auto 10px",
          display: "block",
        }}
      >
        CREATE GUILD
      </motion.button>

      <button
        type="button"
        onClick={onFindGuild}
        className="game-button"
        style={{
          width: "100%",
          maxWidth: 330,
          margin: "0 auto",
          display: "block",
        }}
      >
        FIND A GUILD
      </button>
    </GuildCard>
  );
}

/* =========================================================
 * CREATE GUILD
 * ========================================================= */

function CreateGuild({
  busy,
  error,
  onCancel,
  onSubmit,
}) {
  const [name, setName] =
    useState("");

  const [tag, setTag] =
    useState("");

  const [
    description,
    setDescription,
  ] = useState("");

  const [emblem, setEmblem] =
    useState("shield");

  const [
    joinType,
    setJoinType,
  ] = useState("request");

  const [
    language,
    setLanguage,
  ] = useState("en");

  const [
    minimumLevel,
    setMinimumLevel,
  ] = useState("1");

  const [
    minimumTrophies,
    setMinimumTrophies,
  ] = useState("0");

  const canSubmit =
    !busy &&
    name.trim().length >= 3 &&
    tag.trim().length >= 2;

  const handleSubmit = () => {
    if (!canSubmit) {
      return;
    }

    onSubmit?.({
      name:
        name.trim(),

      tag:
        tag.trim().toUpperCase(),

      description:
        description.trim(),

      emblem,

      joinType,

      language,

      minimumLevel:
        Math.max(
          1,
          Number(
            minimumLevel || 1
          )
        ),

      minimumTrophies:
        Math.max(
          0,
          Number(
            minimumTrophies || 0
          )
        ),
    });
  };

  return (
    <GuildCard>
      <div
        className="shadow1"
        style={{
          marginBottom: 20,
          color: "#fff",
          fontSize: 27,
          lineHeight: "31px",
          fontWeight: 900,
          textAlign: "center",
        }}
      >
        Create Guild
      </div>

      <GuildEmblem
        emblem={emblem}
        size={88}
      />

      <div
        style={{
          marginTop: 8,
          marginBottom: 24,

          color:
            "rgba(255,255,255,.72)",

          fontSize: 14,
          textAlign: "center",
        }}
      >
        Choose your identity
      </div>

      <TextInput
        label="Guild Name"
        value={name}
        maxLength={30}
        placeholder="Moon Warriors"
        onChange={(e) =>
          setName(
            e.target.value
          )
        }
      />

      <TextInput
        label="Guild Tag"
        value={tag}
        maxLength={6}
        placeholder="MOON"
        onChange={(e) => {
          const value =
            e.target.value
              .toUpperCase()
              .replace(
                /[^A-Z0-9]/g,
                ""
              );

          setTag(value);
        }}
      />

      <div
        style={{
          marginBottom: 17,
        }}
      >
        <SectionTitle>
          Description
        </SectionTitle>

        <textarea
          value={description}
          maxLength={200}
          placeholder="Fight together. Rise together."
          onChange={(e) =>
            setDescription(
              e.target.value
            )
          }
          style={{
            width: "100%",
            minHeight: 105,
            boxSizing:
              "border-box",
            padding: 13,
            resize: "none",
            borderRadius: 9,

            border:
              "3px solid rgba(91,48,23,.9)",

            background: "#120906",
            color: "#fff",

            fontSize: 16,
            lineHeight: "21px",

            outline: "none",

            boxShadow:
              "inset 0 2px 6px rgba(0,0,0,.45)",
          }}
        />

        <div
          style={{
            marginTop: 4,

            color:
              "rgba(255,255,255,.5)",

            fontSize: 12,
            textAlign: "right",
          }}
        >
          {description.length}/200
        </div>
      </div>

      <div
        style={{
          marginBottom: 20,
        }}
      >
        <SectionTitle>
          Emblem
        </SectionTitle>

        <div
          style={{
            display: "grid",

            gridTemplateColumns:
              "repeat(4, minmax(0, 1fr))",

            gap: 8,
          }}
        >
          {EMBLEMS.map(
            (item) => {
              const selected =
                item === emblem;

              return (
                <motion.button
                  key={item}
                  type="button"
                  whileTap={{
                    scale: 0.95,
                  }}
                  onClick={() =>
                    setEmblem(item)
                  }
                  style={{
                    height: 70,
                    padding: 5,
                    borderRadius: 10,

                    border:
                      selected
                        ? "3px solid #ffda65"
                        : "3px solid rgba(81,42,19,.8)",

                    background:
                      selected
                        ? "#64401c"
                        : "#26130a",

                    boxShadow:
                      selected
                        ? "0 0 10px rgba(255,215,95,.28)"
                        : "none",

                    cursor:
                      "pointer",
                  }}
                >
                  <GuildEmblem
                    emblem={item}
                    size={48}
                  />
                </motion.button>
              );
            }
          )}
        </div>
      </div>

      <SelectInput
        label="Join Policy"
        value={joinType}
        onChange={(e) =>
          setJoinType(
            e.target.value
          )
        }
      >
        <option value="open">
          Open
        </option>

        <option value="request">
          Request to Join
        </option>

        <option value="invite">
          Invite Only
        </option>
      </SelectInput>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "1fr 1fr",
          gap: 10,
        }}
      >
        <TextInput
          label="Min Level"
          type="number"
          min="1"
          value={minimumLevel}
          onChange={(e) =>
            setMinimumLevel(
              e.target.value
            )
          }
        />

        <TextInput
          label="Min Trophies"
          type="number"
          min="0"
          value={minimumTrophies}
          onChange={(e) =>
            setMinimumTrophies(
              e.target.value
            )
          }
        />
      </div>

      <SelectInput
        label="Language"
        value={language}
        onChange={(e) =>
          setLanguage(
            e.target.value
          )
        }
      >
        <option value="en">
          English
        </option>

        <option value="pt">
          Português
        </option>

        <option value="es">
          Español
        </option>

        <option value="fr">
          Français
        </option>

        <option value="de">
          Deutsch
        </option>

        <option value="tr">
          Türkçe
        </option>

        <option value="other">
          Other
        </option>
      </SelectInput>

      <div
        style={{
          marginTop: 3,
          marginBottom: 17,

          padding:
            "13px 15px",

          display: "flex",

          alignItems: "center",
          justifyContent:
            "space-between",

          borderRadius: 9,

          border:
            "2px solid rgba(255,210,99,.28)",

          background:
            "rgba(35,17,7,.48)",
        }}
      >
        <div>
          <div
            style={{
              color:
                "rgba(255,255,255,.55)",

              fontSize: 11,

              textTransform:
                "uppercase",
            }}
          >
            Creation Cost
          </div>

          <div
            style={{
              marginTop: 2,
              color: "#fff",
              fontSize: 15,
              fontWeight: 700,
            }}
          >
            Create your own Guild
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,

            color: "#ffe06e",

            fontSize: 20,
            fontWeight: 900,
          }}
        >
          <img
            src="/runes.png"
            alt=""
            draggable={false}
            style={{
              width: 25,
              height: 25,

              objectFit:
                "contain",
            }}
          />

          {GUILD_CREATE_COST}
        </div>
      </div>

      {error && (
        <div
          style={{
            marginBottom: 14,

            padding:
              "11px 12px",

            borderRadius: 8,

            border:
              "1px solid rgba(255,100,100,.35)",

            background:
              "rgba(100,0,0,.3)",

            color: "#ffb0b0",

            fontSize: 13,
            lineHeight: "18px",

            textAlign: "center",
          }}
        >
          {error}
        </div>
      )}

      <motion.button
        type="button"
        whileTap={
          canSubmit
            ? {
                scale: 0.98,
              }
            : undefined
        }
        disabled={!canSubmit}
        onClick={handleSubmit}
        className="game-button yellow-button"
        style={{
          width: "100%",
          marginBottom: 8,

          opacity:
            canSubmit
              ? 1
              : 0.5,
        }}
      >
        {busy
          ? "CREATING..."
          : "CREATE GUILD"}
      </motion.button>

      <button
        type="button"
        disabled={busy}
        onClick={onCancel}
        style={{
          width: "100%",
          height: 42,

          border: 0,

          background:
            "transparent",

          color:
            "rgba(255,255,255,.7)",

          fontSize: 14,
          fontWeight: 700,

          cursor:
            busy
              ? "default"
              : "pointer",

          opacity:
            busy
              ? 0.5
              : 1,
        }}
      >
        CANCEL
      </button>
    </GuildCard>
  );
}

/* =========================================================
 * INFO BOX
 * ========================================================= */

function InfoBox({
  label,
  value,
}) {
  return (
    <div
      style={{
        minWidth: 0,
        padding: 11,
        borderRadius: 8,

        border:
          "2px solid rgba(79,40,18,.75)",

        background:
          "rgba(34,16,7,.5)",

        textAlign: "center",
      }}
    >
      <div
        style={{
          color:
            "rgba(255,255,255,.48)",

          fontSize: 10,
          lineHeight: "13px",

          textTransform:
            "uppercase",
        }}
      >
        {label}
      </div>

      <div
        style={{
          marginTop: 3,

          overflow: "hidden",

          color: "#fff",

          fontSize: 14,
          fontWeight: 800,

          textOverflow:
            "ellipsis",

          whiteSpace: "nowrap",
        }}
      >
        {value}
      </div>
    </div>
  );
}

/* =========================================================
 * MY GUILD
 * ========================================================= */

function MyGuild({
  guild,
  onOpenMembers,
  onOpenRequests,
}) {
  const canManageRequests =
    guild.role === "leader" ||
    guild.role === "officer";

  return (
    <GuildCard
      style={{
        paddingTop: 22,
      }}
    >
      <GuildEmblem
        emblem={guild.emblem}
        size={105}
      />

      <div
        className="shadow1"
        style={{
          marginTop: 7,
          color: "#fff",

          fontSize: 28,
          lineHeight: "32px",

          fontWeight: 900,
          textAlign: "center",

          wordBreak:
            "break-word",
        }}
      >
        {guild.name}
      </div>

      <div
        style={{
          marginTop: 2,

          color: "#ffd86a",

          fontSize: 16,
          fontWeight: 900,

          textAlign: "center",
        }}
      >
        [{guild.tag}]
      </div>

      <div
        style={{
          marginTop: 15,

          display: "flex",
          alignItems: "center",
          justifyContent:
            "center",

          gap: 7,

          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            padding:
              "7px 12px",

            borderRadius: 20,

            border:
              "1px solid rgba(255,216,105,.35)",

            background:
              "rgba(27,12,5,.5)",

            color: "#fff",

            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {Number(
            guild.memberCount || 0
          )}
          {" / "}
          {Number(
            guild.maxMembers || 10
          )}
          {" MEMBERS"}
        </div>

        <div
          style={{
            padding:
              "7px 12px",

            borderRadius: 20,

            border:
              "1px solid rgba(255,216,105,.35)",

            background:
              "rgba(27,12,5,.5)",

            color: "#ffd86a",

            fontSize: 13,
            fontWeight: 800,

            textTransform:
              "uppercase",
          }}
        >
          {guild.role}
        </div>
      </div>

      {guild.description && (
        <div
          style={{
            marginTop: 18,
            padding: 13,
            borderRadius: 8,

            background:
              "rgba(30,14,6,.35)",

            color:
              "rgba(255,255,255,.82)",

            fontSize: 14,
            lineHeight: "20px",

            textAlign: "center",

            wordBreak:
              "break-word",
          }}
        >
          {guild.description}
        </div>
      )}

      <div
        style={{
          marginTop: 17,

          display: "grid",

          gridTemplateColumns:
            "1fr 1fr",

          gap: 8,
        }}
      >
        <InfoBox
          label="Join Policy"
          value={
            guild.joinType ===
            "request"
              ? "Request"
              : guild.joinType ===
                  "invite"
                ? "Invite Only"
                : "Open"
          }
        />

        <InfoBox
          label="Language"
          value={String(
            guild.language ||
              "en"
          ).toUpperCase()}
        />

        <InfoBox
          label="Minimum Level"
          value={
            guild.minimumLevel ??
            1
          }
        />

        <InfoBox
          label="Minimum Trophies"
          value={
            guild.minimumTrophies ??
            0
          }
        />
      </div>

      <div
        style={{
          marginTop: 18,

          display: "grid",

          gridTemplateColumns:
            "1fr 1fr",

          gap: 8,
        }}
      >
        <button
          type="button"
          className="game-button-small yellow-button"
          style={{
            width: "100%",
            margin: 0,
          }}
          onClick={
            onOpenMembers
          }
        >
          MEMBERS
        </button>

        {canManageRequests && (
          <button
            type="button"
            className="game-button-small yellow-button"
            style={{
              width: "100%",
              margin: 0,
            }}
            onClick={
              onOpenRequests
            }
          >
            REQUESTS
            {Number(
              guild.pendingRequests ||
                0
            ) > 0
              ? ` (${guild.pendingRequests})`
              : ""}
          </button>
        )}

        <button
          type="button"
          disabled
          className="game-button-small"
          style={{
            width: "100%",
            margin: 0,
            opacity: 0.45,
          }}
        >
          ACTIVITY
        </button>
      </div>
    </GuildCard>
  );
}

/* =========================================================
 * MEMBERS
 * ========================================================= */

function GuildMembers({
  members,
  loading,
  error,
  viewerRole,
  busyUserId,
  onChangeRole,
}) {
  const canManageRoles =
    viewerRole === "leader";

  return (
    <GuildCard>
      <div
        className="shadow1"
        style={{
          marginBottom: 18,
          color: "#fff",

          fontSize: 25,
          fontWeight: 900,

          textAlign: "center",
        }}
      >
        Members
      </div>

      {error && (
        <div
          style={{
            padding: 10,
            marginBottom: 12,

            borderRadius: 8,

            background:
              "rgba(100,0,0,.3)",

            border:
              "1px solid rgba(255,100,100,.3)",

            color: "#ffaaaa",

            fontSize: 13,

            textAlign: "center",
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <LoadingSpinner />
      ) : members.length === 0 ? (
        <div
          style={{
            padding:
              "25px 10px",

            color:
              "rgba(255,255,255,.7)",

            textAlign: "center",
          }}
        >
          No members found.
        </div>
      ) : (
        members.map(
          (member) => {
            const changing =
              String(
                busyUserId || ""
              ) ===
              String(
                member.userId
              );

            const manageable =
              canManageRoles &&
              member.role !==
                "leader";

            return (
              <div
                key={
                  member.userId
                }
                style={{
                  padding: 12,

                  marginBottom: 10,

                  borderRadius: 8,

                  background:
                    "rgba(0,0,0,.25)",

                  border:
                    "2px solid rgba(79,40,18,.75)",
                }}
              >
                <div
                  style={{
                    display: "flex",

                    alignItems:
                      "center",

                    justifyContent:
                      "space-between",

                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    <div
                      style={{
                        color: "#fff",

                        fontSize: 17,
                        fontWeight: 800,

                        overflow:
                          "hidden",

                        textOverflow:
                          "ellipsis",

                        whiteSpace:
                          "nowrap",
                      }}
                    >
                      {member.username ||
                        "Player"}
                    </div>

                    <div
                      style={{
                        marginTop: 4,

                        color:
                          "rgba(255,255,255,.65)",

                        fontSize: 13,
                      }}
                    >
                      Level{" "}
                      {Number(
                        member.level ||
                          0
                      )}

                      {" • "}

                      {Number(
                        member.trophies ||
                          0
                      )}{" "}
                      Trophies
                    </div>
                  </div>

                  <div
                    style={{
                      flex:
                        "0 0 auto",

                      padding:
                        "5px 9px",

                      borderRadius:
                        12,

                      background:
                        "rgba(0,0,0,.35)",

                      color:
                        member.role ===
                        "leader"
                          ? "#ffd86a"
                          : "#fff",

                      fontSize: 11,

                      fontWeight: 800,

                      textTransform:
                        "uppercase",
                    }}
                  >
                    {member.role}
                  </div>
                </div>

                {manageable && (
                  <button
                    type="button"

                    disabled={
                      changing
                    }

                    className={
                      member.role ===
                      "member"
                        ? "game-button-small yellow-button"
                        : "game-button-small"
                    }

                    style={{
                      width: "100%",

                      marginTop: 10,
                      marginBottom: 0,

                      opacity:
                        changing
                          ? 0.6
                          : 1,
                    }}

                    onClick={() => {
                      const newRole =
                        member.role ===
                        "member"
                          ? "officer"
                          : "member";

                      const action =
                        newRole ===
                        "officer"
                          ? "promote"
                          : "demote";

                      const confirmed =
                        window.confirm(
                          `Are you sure you want to ${action} ${member.username || "this player"}?`
                        );

                      if (
                        !confirmed
                      ) {
                        return;
                      }

                      onChangeRole(
                        member.userId,
                        newRole
                      );
                    }}
                  >
                    {changing
                      ? "UPDATING..."
                      : member.role ===
                          "member"
                        ? "PROMOTE TO OFFICER"
                        : "DEMOTE TO MEMBER"}
                  </button>
                )}
              </div>
            );
          }
        )
      )}
    </GuildCard>
  );
}

/* =========================================================
 * REQUESTS
 * ========================================================= */

function GuildRequests({
  requests,
  loading,
  busyId,
  error,
  onApprove,
  onReject,
}) {
  return (
    <GuildCard>
      <div
        className="shadow1"
        style={{
          marginBottom: 18,
          color: "#fff",
          fontSize: 25,
          fontWeight: 900,
          textAlign: "center",
        }}
      >
        Join Requests
      </div>

      {error && (
        <div
          style={{
            padding: 10,
            marginBottom: 12,

            borderRadius: 8,

            background:
              "rgba(100,0,0,.3)",

            border:
              "1px solid rgba(255,100,100,.3)",

            color: "#ffaaaa",

            fontSize: 13,

            textAlign: "center",
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <LoadingSpinner />
      ) : requests.length === 0 ? (
        <div
          style={{
            padding:
              "25px 10px",

            color:
              "rgba(255,255,255,.7)",

            textAlign: "center",
          }}
        >
          No pending requests.
        </div>
      ) : (
        requests.map(
          (request) => {
            const isBusy =
              busyId ===
              request.id;

            return (
              <div
                key={request.id}
                style={{
                  padding: 12,
                  marginBottom: 10,

                  borderRadius: 8,

                  background:
                    "rgba(0,0,0,.25)",

                  border:
                    "2px solid rgba(79,40,18,.75)",
                }}
              >
                <div
                  style={{
                    color: "#fff",

                    fontSize: 17,
                    fontWeight: 800,
                  }}
                >
                  {request.username ||
                    "Player"}
                </div>

                <div
                  style={{
                    marginTop: 4,

                    color:
                      "rgba(255,255,255,.65)",

                    fontSize: 13,
                  }}
                >
                  Level{" "}
                  {Number(
                    request.level ||
                      0
                  )}

                  {" • "}

                  {Number(
                    request.trophies ||
                      0
                  )}{" "}
                  Trophies
                </div>

                <div
                  style={{
                    display: "grid",

                    gridTemplateColumns:
                      "1fr 1fr",

                    gap: 8,

                    marginTop: 12,
                  }}
                >
                  <button
                    type="button"

                    disabled={
                      isBusy
                    }

                    className="game-button-small yellow-button"

                    style={{
                      width:
                        "100%",
                      margin: 0,
                    }}

                    onClick={() =>
                      onApprove(
                        request.id
                      )
                    }
                  >
                    {isBusy
                      ? "..."
                      : "ACCEPT"}
                  </button>

                  <button
                    type="button"

                    disabled={
                      isBusy
                    }

                    className="game-button-small"

                    style={{
                      width:
                        "100%",
                      margin: 0,
                    }}

                    onClick={() =>
                      onReject(
                        request.id
                      )
                    }
                  >
                    {isBusy
                      ? "..."
                      : "REJECT"}
                  </button>
                </div>
              </div>
            );
          }
        )
      )}
    </GuildCard>
  );
}

/* =========================================================
 * MAIN
 * ========================================================= */

export default function Guilds({
  user,
  setUser,
  onNavigate,
}) {
  const [loading, setLoading] =
    useState(true);

  const [guild, setGuild] =
    useState(null);

  const [view, setView] =
    useState("main");

  const [busy, setBusy] =
    useState(false);

  const [error, setError] =
    useState("");

  /* MEMBERS */

  const [
    members,
    setMembers,
  ] = useState([]);

  const [
    membersLoading,
    setMembersLoading,
  ] = useState(false);

  const [
    membersError,
    setMembersError,
  ] = useState("");

  const [
    memberRoleBusyUserId,
    setMemberRoleBusyUserId,
  ] = useState(null);

  /* REQUESTS */

  const [
    requests,
    setRequests,
  ] = useState([]);

  const [
    requestsLoading,
    setRequestsLoading,
  ] = useState(false);

  const [
    requestBusyId,
    setRequestBusyId,
  ] = useState(null);

  const [
    requestsError,
    setRequestsError,
  ] = useState("");

  /* =========================================================
   * LOAD MY GUILD
   * ========================================================= */

  async function loadGuild({
    silent = false,
  } = {}) {
    if (!user?.id) {
      return;
    }

    try {
      if (!silent) {
        setLoading(true);
      }

      setError("");

      const data =
        await apiJson(
          "/api/guilds/my-guild"
        );

      setGuild(
        data?.guild ||
          null
      );
    } catch (err) {
      console.error(
        "[GUILDS LOAD]",
        err
      );

      setError(
        "Could not load your Guild."
      );
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    loadGuild();
  }, [user?.id]);

  /* =========================================================
   * CREATE GUILD
   * ========================================================= */

  async function createGuild(
    payload
  ) {
    if (busy) {
      return;
    }

    const token =
      localStorage.getItem(
        "token"
      );

    if (!token) {
      setError(
        "You are not authenticated."
      );

      return;
    }

    try {
      setBusy(true);
      setError("");

      const response =
        await fetch(
          `${API_BASE}/api/guilds`,
          {
            method: "POST",

            headers: {
              "Content-Type":
                "application/json",

              Authorization:
                `Bearer ${token}`,

              "X-TG-INITDATA":
                window.Telegram
                  ?.WebApp
                  ?.initData ||
                "",
            },

            body:
              JSON.stringify(
                payload
              ),
          }
        );

      const data =
        await response
          .json()
          .catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.error ||
            "GUILD_CREATE_FAILED"
        );
      }

      setGuild(
        data?.guild ||
          null
      );

      if (
        setUser &&
        data?.user
      ) {
        setUser(
          (prev) => ({
            ...prev,

            runes:
              data.user
                .runes ??
              prev?.runes,

            sparks:
              data.user
                .sparks ??
              prev?.sparks,
          })
        );
      }

      setView("main");
      setError("");
    } catch (err) {
      console.error(
        "[GUILD CREATE]",
        err
      );

      setError(
        errorMessage(
          err?.message
        )
      );
    } finally {
      setBusy(false);
    }
  }

  /* =========================================================
   * MEMBERS
   * ========================================================= */

  async function loadMembers() {
    try {
      setMembersLoading(
        true
      );

      setMembersError("");

      const data =
        await apiJson(
          "/api/guilds/my-guild/members"
        );

      setMembers(
        Array.isArray(
          data?.members
        )
          ? data.members
          : []
      );
    } catch (err) {
      console.error(
        "[GUILD MEMBERS]",
        err
      );

      setMembersError(
        "Could not load members."
      );

      setMembers([]);
    } finally {
      setMembersLoading(
        false
      );
    }
  }

  async function openMembers() {
    setMembersError("");

    setView("members");

    await loadMembers();
  }

  async function changeMemberRole(
    memberUserId,
    role
  ) {
    if (
      memberRoleBusyUserId !==
      null
    ) {
      return;
    }

    const token =
      localStorage.getItem(
        "token"
      );

    if (!token) {
      setMembersError(
        "You are not authenticated."
      );

      return;
    }

    try {
      setMemberRoleBusyUserId(
        memberUserId
      );

      setMembersError("");

      const response =
        await fetch(
          `${API_BASE}/api/guilds/my-guild/members/${encodeURIComponent(
            memberUserId
          )}/role`,
          {
            method: "PATCH",

            headers: {
              "Content-Type":
                "application/json",

              Authorization:
                `Bearer ${token}`,

              "X-TG-INITDATA":
                window.Telegram
                  ?.WebApp
                  ?.initData ||
                "",
            },

            body:
              JSON.stringify({
                role,
              }),
          }
        );

      const data =
        await response
          .json()
          .catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.error ||
            "GUILD_ROLE_CHANGE_FAILED"
        );
      }

      /*
       * Update immediately on screen.
       */
      setMembers(
        (prev) =>
          prev.map(
            (member) =>
              String(
                member.userId
              ) ===
              String(
                memberUserId
              )
                ? {
                    ...member,
                    role:
                      data?.member
                        ?.role ||
                      role,
                  }
                : member
          )
      );

      /*
       * Reload server version.
       */
      await loadMembers();
    } catch (err) {
      console.error(
        "[GUILD ROLE CHANGE]",
        err
      );

      setMembersError(
        errorMessage(
          err?.message
        )
      );
    } finally {
      setMemberRoleBusyUserId(
        null
      );
    }
  }

  /* =========================================================
   * REQUESTS
   * ========================================================= */

  async function loadRequests() {
    try {
      setRequestsLoading(
        true
      );

      setRequestsError("");

      const data =
        await apiJson(
          "/api/guilds/my-guild/requests"
        );

      setRequests(
        Array.isArray(
          data?.requests
        )
          ? data.requests
          : []
      );
    } catch (err) {
      console.error(
        "[GUILD REQUESTS]",
        err
      );

      setRequestsError(
        "Could not load requests."
      );

      setRequests([]);
    } finally {
      setRequestsLoading(
        false
      );
    }
  }

  async function openRequests() {
    setRequestsError("");

    setView("requests");

    await loadRequests();
  }

  async function approveRequest(
    requestId
  ) {
    if (
      requestBusyId !==
      null
    ) {
      return;
    }

    const token =
      localStorage.getItem(
        "token"
      );

    if (!token) {
      setRequestsError(
        "You are not authenticated."
      );

      return;
    }

    try {
      setRequestBusyId(
        requestId
      );

      setRequestsError("");

      const response =
        await fetch(
          `${API_BASE}/api/guilds/requests/${requestId}/approve`,
          {
            method: "POST",

            headers: {
              Authorization:
                `Bearer ${token}`,

              "X-TG-INITDATA":
                window.Telegram
                  ?.WebApp
                  ?.initData ||
                "",
            },
          }
        );

      const data =
        await response
          .json()
          .catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.error ||
            "APPROVE_FAILED"
        );
      }

      setRequests(
        (prev) =>
          prev.filter(
            (request) =>
              String(
                request.id
              ) !==
              String(
                requestId
              )
          )
      );

      await loadGuild({
        silent: true,
      });

      await loadRequests();
    } catch (err) {
      console.error(
        "[GUILD APPROVE]",
        err
      );

      setRequestsError(
        errorMessage(
          err?.message
        )
      );

      await loadRequests();
    } finally {
      setRequestBusyId(
        null
      );
    }
  }

  async function rejectRequest(
    requestId
  ) {
    if (
      requestBusyId !==
      null
    ) {
      return;
    }

    const token =
      localStorage.getItem(
        "token"
      );

    if (!token) {
      setRequestsError(
        "You are not authenticated."
      );

      return;
    }

    try {
      setRequestBusyId(
        requestId
      );

      setRequestsError("");

      const response =
        await fetch(
          `${API_BASE}/api/guilds/requests/${requestId}/reject`,
          {
            method: "POST",

            headers: {
              Authorization:
                `Bearer ${token}`,

              "X-TG-INITDATA":
                window.Telegram
                  ?.WebApp
                  ?.initData ||
                "",
            },
          }
        );

      const data =
        await response
          .json()
          .catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.error ||
            "REJECT_FAILED"
        );
      }

      setRequests(
        (prev) =>
          prev.filter(
            (request) =>
              String(
                request.id
              ) !==
              String(
                requestId
              )
          )
      );

      await loadGuild({
        silent: true,
      });

      await loadRequests();
    } catch (err) {
      console.error(
        "[GUILD REJECT]",
        err
      );

      setRequestsError(
        errorMessage(
          err?.message
        )
      );

      await loadRequests();
    } finally {
      setRequestBusyId(
        null
      );
    }
  }

  /* =========================================================
   * LOADING
   * ========================================================= */

  if (loading) {
    return (
      <div
        style={{
          width: "100%",
          minHeight: 300,

          display: "flex",

          alignItems:
            "center",

          justifyContent:
            "center",
        }}
      >
        <LoadingSpinner />
      </div>
    );
  }

  /* =========================================================
   * PAGE TITLE
   * ========================================================= */

  let pageTitle =
    "GUILDS";

  if (
    view ===
    "members"
  ) {
    pageTitle =
      "MEMBERS";
  }

  if (
    view ===
    "requests"
  ) {
    pageTitle =
      "REQUESTS";
  }

  /* =========================================================
   * PAGE
   * ========================================================= */

  return (
    <div
      style={{
        width: "100%",

        height:
          "calc(100dvh - 155px)",

        minHeight: 0,

        overflowY: "auto",

        overflowX:
          "hidden",

        WebkitOverflowScrolling:
          "touch",

        overscrollBehaviorY:
          "contain",

        touchAction:
          "pan-y",

        boxSizing:
          "border-box",

        padding:
          "0 13px 55px",

        scrollbarWidth:
          "thin",
      }}
    >
      <GuildPageHeader
        title={pageTitle}

        onBack={() => {
          if (
            view ===
              "create" ||
            view ===
              "members" ||
            view ===
              "requests"
          ) {
            setError("");
            setMembersError("");
            setRequestsError("");

            setView(
              "main"
            );

            return;
          }

          onNavigate?.(
            "gamesHub"
          );
        }}
      />

      {view ===
      "create" ? (
        <CreateGuild
          busy={busy}
          error={error}

          onCancel={() => {
            if (busy) {
              return;
            }

            setError("");

            setView(
              "main"
            );
          }}

          onSubmit={
            createGuild
          }
        />
      ) : view ===
        "members" ? (
        <GuildMembers
          members={
            members
          }

          loading={
            membersLoading
          }

          error={
            membersError
          }

          viewerRole={
            guild?.role
          }

          busyUserId={
            memberRoleBusyUserId
          }

          onChangeRole={
            changeMemberRole
          }
        />
      ) : view ===
        "requests" ? (
        <GuildRequests
          requests={
            requests
          }

          loading={
            requestsLoading
          }

          busyId={
            requestBusyId
          }

          error={
            requestsError
          }

          onApprove={
            approveRequest
          }

          onReject={
            rejectRequest
          }
        />
      ) : guild ? (
        <MyGuild
          guild={guild}

          onOpenMembers={
            openMembers
          }

          onOpenRequests={
            openRequests
          }
        />
      ) : (
        <>
          {error && (
            <div
              style={{
                marginBottom:
                  10,

                padding:
                  "10px 12px",

                borderRadius:
                  8,

                background:
                  "rgba(100,0,0,.32)",

                border:
                  "1px solid rgba(255,100,100,.32)",

                color:
                  "#ffb0b0",

                fontSize:
                  13,

                textAlign:
                  "center",
              }}
            >
              {error}
            </div>
          )}

          <NoGuild
            onCreate={() => {
              setError("");

              setView(
                "create"
              );
            }}

            onFindGuild={() => {
              onNavigate?.(
                "guildBrowser"
              );
            }}
          />
        </>
      )}
    </div>
  );
}